import { NavLink } from 'react-router-dom';
import type { ResolvedNavGroup } from '../../shared/nav/canonical-nav.js';
import { NavIcon } from '../../shared/nav/icons.js';

/**
 * Сайдбар по дизайну manager v3 §2.2 — на всю высоту, бежевый до низа.
 *
 * Раскладка сверху вниз:
 *   1. Бренд «LovArt · <Отель>».
 *   2. Сгруппированная навигация (заголовок группы + пункты, Phosphor-иконки).
 *      Активный пункт: charcoal-фон + champagne-текст/иконка.
 *   3. Карточка пользователя ВНИЗУ: аватар-инициалы + имя + роль/email +
 *      кнопка «Выйти» (logout перенесён сюда из убранного топбара).
 *
 * Desktop (≥1024): статичная колонка. Tablet (768–1023): тот же сайдбар
 * работает как DRAWER (скрыт за гамбургером, открывается по `open`).
 * Phone (<768): скрыт — навигация через нижний таб-бар (BottomTabs).
 */
export interface SidebarProps {
  groups: readonly ResolvedNavGroup[];
  /** Готовый бренд «LovArt · <Отель>». */
  brandFull: string;
  /** Название отеля под вордмарком. */
  hotelName: string;
  actorName: string;
  /** Подпись под именем (email либо роль). */
  actorEmail: string;
  userInitials: string;
  logoutLabel: string;
  onLogout: () => void;
  /** Открыт ли сайдбар-drawer (планшет). На десктопе игнорируется CSS. */
  open: boolean;
  /** Закрыть drawer (клик по пункту / бэкдропу — планшет). */
  onClose: () => void;
}

export function Sidebar({
  groups,
  brandFull,
  hotelName,
  actorName,
  actorEmail,
  userInitials,
  logoutLabel,
  onLogout,
  open,
  onClose,
}: SidebarProps): JSX.Element {
  return (
    <>
      <div
        className={`ta-sidebar__scrim${open ? ' open' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={`ta-sidebar${open ? ' open' : ''}`}
        aria-label="Основная навигация"
      >
        <div className="ta-sidebar__brand" data-testid="sidebar-brand">
          {brandFull}
        </div>
        <div className="ta-sidebar__meta">{hotelName}</div>

        <nav className="ta-sidebar__nav">
          {groups.map((group) => (
            <div key={group.key} className="ta-sidebar__group">
              <div className="ta-sidebar__group-title">{group.label}</div>
              {group.items.map((item) => (
                <NavLink
                  key={item.key}
                  to={item.path}
                  end={item.path === '/'}
                  onClick={onClose}
                  className={({ isActive }) =>
                    `ta-sidebar__link${isActive ? ' active' : ''}`
                  }
                  data-testid={`nav-${item.key}`}
                >
                  <NavIcon name={item.icon} size={18} className="ta-sidebar__icon" />
                  <span className="ta-sidebar__label">{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        {/* Карточка пользователя + «Выйти» (§2.2 — низ сайдбара). */}
        <div className="ta-sidebar__user" data-testid="sidebar-user">
          <span className="ta-avatar" aria-hidden="true">
            {userInitials}
          </span>
          <div className="ta-sidebar__user-meta">
            <div className="ta-sidebar__user-name">{actorName}</div>
            <div className="ta-sidebar__user-sub">{actorEmail}</div>
          </div>
          <button
            type="button"
            className="ta-sidebar__logout"
            onClick={onLogout}
            data-testid="sidebar-logout"
            aria-label={logoutLabel}
            title={logoutLabel}
          >
            <NavIcon name="sign-out" size={16} className="ta-sidebar__logout-icon" />
            <span>{logoutLabel}</span>
          </button>
        </div>
      </aside>
    </>
  );
}
